import KPICards from '../Components/KPICards';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../Components/Table';
import {
  kpiInvoiceData,
  invoiceTrendData,
  statusData,
  agingData,
} from '../Data/mockData';

export default function Invoice() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* KPI Cards */}
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', marginBottom: 16 }}>
          Invoice Overview
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          {kpiInvoiceData.map((kpi, index) => (
            <KPICards
              key={index}
              label={kpi.label}
              value={kpi.value}
              trend={kpi.trend}
              bgColor={kpi.bgColor}
            />
          ))}
        </div>
      </div>

      {/* Invoice Trends */}
      <div style={{ backgroundColor: '#ffffff', borderRadius: 8, border: '1px solid #e5e7eb', padding: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', marginBottom: 16 }}>
          Invoice Value Trends
        </h2>

        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={invoiceTrendData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip formatter={(value) => `AED ${(value / 1000).toFixed(0)}K`} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="generated" stroke="#3b82f6" strokeWidth={2} name="Generated" />
            <Line type="monotone" dataKey="paid" stroke="#10b981" strokeWidth={2} name="Paid" />
            <Line type="monotone" dataKey="unpaid" stroke="#f59e0b" strokeWidth={2} name="Unpaid" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Invoice Status Distribution */}
      <div style={{ backgroundColor: '#ffffff', borderRadius: 8, border: '1px solid #e5e7eb', padding: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', marginBottom: 16 }}>
          Invoice Status Distribution
        </h2>

        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={statusData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="status" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="count" name="Invoice Count">
              {statusData.map((entry, index) => (
                <rect
                  key={`cell-${index}`}
                  fill={
                    entry.status === 'Paid'
                      ? '#10b981'
                      : entry.status === 'Overdue'
                      ? '#ef4444'
                      : '#f59e0b'
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Invoice Aging Table */}
      <div style={{ backgroundColor: '#ffffff', borderRadius: 8, border: '1px solid #e5e7eb' }}>
        <div style={{ padding: 24, borderBottom: '1px solid #e5e7eb' }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827' }}>
            Invoice Aging Details
          </h2>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice ID</TableHead>
                <TableHead>Corporate</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Age Bucket</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Due Date</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {agingData.map((row, index) => {
                const ageStyles =
                  row.ageBucket === '0-30 days'
                    ? { backgroundColor: '#dcfce7', color: '#15803d' }
                    : row.ageBucket === '31-60 days'
                    ? { backgroundColor: '#dbeafe', color: '#1d4ed8' }
                    : row.ageBucket === '61-90 days'
                    ? { backgroundColor: '#ffedd5', color: '#c2410c' }
                    : { backgroundColor: '#fee2e2', color: '#b91c1c' };

                const statusStyles =
                  row.status === 'Overdue'
                    ? { backgroundColor: '#fee2e2', color: '#b91c1c' }
                    : { backgroundColor: '#ffedd5', color: '#c2410c' };

                return (
                  <TableRow
                    key={index}
                    style={{
                      backgroundColor: row.status === 'Overdue' ? '#fef2f2' : undefined,
                    }}
                  >
                    <TableCell style={{ fontWeight: 500 }}>{row.invoiceId}</TableCell>
                    <TableCell>{row.corporate}</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>{row.amount}</TableCell>
                    <TableCell>
                      <span
                        style={{
                          display: 'inline-flex',
                          padding: '4px 8px',
                          fontSize: 12,
                          fontWeight: 500,
                          borderRadius: 9999,
                          ...ageStyles,
                        }}
                      >
                        {row.ageBucket}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span
                        style={{
                          display: 'inline-flex',
                          padding: '4px 8px',
                          fontSize: 12,
                          fontWeight: 500,
                          borderRadius: 9999,
                          ...statusStyles,
                        }}
                      >
                        {row.status}
                      </span>
                    </TableCell>
                    <TableCell style={{ fontSize: 14 }}>{row.dueDate}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        {[
          {
            title: 'Total Invoice Value',
            value: 'AED 126.5M',
            note: '+14% from last month',
            bg: 'linear-gradient(135deg, #ecfdf5, #ccfbf1)',
            color: '#15803d',
          },
          {
            title: 'Avg Invoice Value',
            value: 'AED 14,960',
            note: 'Median: AED 12,500',
            bg: 'linear-gradient(135deg, #eff6ff, #e0e7ff)',
            color: '#1d4ed8',
          },
          {
            title: 'Collection Rate',
            value: '81.5%',
            note: 'Within 30 days',
            bg: 'linear-gradient(135deg, #fff7ed, #fef9c3)',
            color: '#c2410c',
          },
          {
            title: 'Avg Payment Time',
            value: '24 days',
            note: '-3 days improvement',
            bg: 'linear-gradient(135deg, #f5f3ff, #fce7f3)',
            color: '#7c3aed',
          },
        ].map((card, index) => (
          <div
            key={index}
            style={{
              background: card.bg,
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              padding: 16,
            }}
          >
            <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 4 }}>
              {card.title}
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>
              {card.value}
            </div>
            <div style={{ fontSize: 12, color: card.color, marginTop: 4 }}>
              {card.note}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
